import { hasFirebaseConfig } from '../../config/firebaseConfig';
import { useFirebaseEmulators, isProductionEnv, getAppEnv } from '../../config/env';
import { initRemoteConfig } from './RemoteConfigService';
import { readRemoteConfigFlags } from './remoteConfigFlags';
import { initPerformance } from './PerformanceService';
import { initAppCheck } from './AppCheckService';
import { initCrashReporting } from './CrashReportingService';

/** Platform bootstrap snapshot (Remote Config, App Check, etc.). */
let platformStatus: Record<string, unknown> | null = null;

export async function bootstrapFirebasePlatform() {
  if (!hasFirebaseConfig()) {
    platformStatus = { mode: 'unconfigured', features: {} };
    return platformStatus;
  }

  const firebaseMod = await import('../../config/firebase.js');
  const app = firebaseMod.default;

  const [remoteConfig, appCheck] = await Promise.all([
    initRemoteConfig(app),
    initAppCheck(app),
  ]);

  const flags = readRemoteConfigFlags();
  const performance = initPerformance(app);
  const crashReporting = initCrashReporting();

  platformStatus = {
    mode: useFirebaseEmulators() ? 'emulator' : 'live',
    env: getAppEnv(),
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    gapiTransport: firebaseMod.getGapiTransportMode?.() ?? null,
    gapiOrigin: firebaseMod.getGapiConnectivity?.()?.proxyOrigin ?? null,
    gapiReason: firebaseMod.getGapiConnectivity?.()?.reason ?? null,
    features: {
      auth: true,
      firestore: true,
      storage: true,
      functions: true,
      hosting: isProductionEnv(),
      messaging: !useFirebaseEmulators(),
      remoteConfig,
      performance,
      appCheck,
      crashReporting,
      phoneAuth: flags.phoneAuth,
      aiAssist: flags.aiAssist,
      rag: flags.rag,
      liveSupport: flags.liveSupport,
      auditExport: flags.auditExport,
      devPanel: flags.devPanel,
      maintenanceMode: flags.maintenanceMode,
      appHosting: false,
      aiLogic: flags.aiAssist,
      genkit: flags.rag,
      releaseMonitoring: isProductionEnv(),
    },
    remoteConfig: flags,
  };

  if (useFirebaseEmulators()) {
    console.info(
      '[Firebase] Emulator mode — UI:',
      `http://${import.meta.env.VITE_FIREBASE_EMULATOR_HOST || '127.0.0.1'}:${import.meta.env.VITE_FIREBASE_UI_EMULATOR_PORT || 4000}`,
    );
  } else if (isProductionEnv()) {
    console.info('[Firebase] Live production backend:', platformStatus.projectId);
  }

  return platformStatus;
}

export function getPlatformStatus() {
  return platformStatus;
}

export function isEmulatorMode() {
  return platformStatus?.mode === 'emulator';
}

export function isLiveMode() {
  return platformStatus?.mode === 'live';
}
